import { z } from "zod";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
import { CreditLedgerEntrySchema } from "./credits.js";
/**
 * Admin-only API surface (`/admin/**`). Every route here is gated server-side
 * on the caller's membership of the Cognito `admins` group (surfaced as the
 * `cognito:groups` claim); see `requireAdmin` in the services runtime. These
 * schemas describe a support/operations console — list users, inspect one
 * user's profile + credits + groups, adjust a balance, and audit the ledger —
 * and are intentionally separate from the self-service `/users/me` surface.
 */
/** One row in the admin user list (`GET /admin/users`). */
export const AdminUserSummarySchema = z.strictObject({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(64),
    email: z.string().email().or(z.literal("")),
    preferredCurrency: SupportedCurrencyCodesSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** A group an admin-inspected user belongs to, with their role in it. */
export const AdminUserGroupSchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string(),
    currency: SupportedCurrencyCodesSchema,
    role: z.enum(["OWNER", "MEMBER", "VIEWER"]),
});
/**
 * Full admin view of one user (`GET /admin/users/{userId}`): their profile, the
 * current credit balance with a recent slice of the ledger, and every group
 * they participate in.
 */
export const AdminUserDetailSchema = z.strictObject({
    user: AdminUserSummarySchema,
    credits: z.strictObject({
        balance: z.number().int().nonnegative(),
        ledger: z.array(CreditLedgerEntrySchema),
    }),
    groups: z.array(AdminUserGroupSchema),
});
/**
 * Body of `POST /admin/users/{userId}/credits` — adjust a user's credit
 * balance. `delta` is a signed, non-zero integer (positive grants, negative
 * deducts); a deduction that would take the balance below zero is rejected
 * (422). `reason` is a short free-text note recorded on the ledger entry for
 * the audit trail.
 */
export const AdminAdjustCreditsRequestSchema = z.strictObject({
    delta: z
        .number()
        .int()
        .refine((n) => n !== 0, { message: "delta must be non-zero" }),
    reason: z.string().min(1).max(280).optional(),
});
/** Response of `POST /admin/users/{userId}/credits`: the post-adjustment balance. */
export const AdminAdjustCreditsResponseSchema = z.strictObject({
    balance: z.number().int().nonnegative(),
});
